import { ampli } from '@/ampli';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { LaunchpadId } from '@/constants/launchpads';

import { GridIcon } from '@/ui/Icons/GridIcon';
import { Multiselect, MultiselectItem } from '@/ui/Multiselect';

import {
  DiscoveryFilters,
  DiscoveryLaunchpad,
  DiscoveryPreset,
  DiscoveryZoraCreatorResult,
} from '@/utils/discovery/types';
import { fetcher } from '@/utils/fetcher';
import { routes } from '@/utils/routes';

type CreatorFilterProps = {
  preset: DiscoveryPreset | null;
  filters: DiscoveryFilters;
  selectedSocialTypes?: DiscoveryLaunchpad[];
  onFiltersChange: (filters: DiscoveryFilters) => void;
};

export const CreatorFilter = ({
  preset,
  filters,
  selectedSocialTypes,
  onFiltersChange,
}: CreatorFilterProps) => {
  const { data: topZoraCreators, isLoading: isLoadingCreators } = useQuery<
    DiscoveryZoraCreatorResult[] | undefined,
    Error
  >({
    queryKey: ['discovery', 'top-zora-creators'],
    queryFn: () => fetcher(routes.api.DISCOVERY_TOP_ZORA_CREATORS),
  });

  const creatorItems: MultiselectItem[] = (topZoraCreators ?? []).map((creator) => ({
    label: creator.name,
    value: creator.address,
    icon: creator.logo,
    iconAlt: `${creator.name} logo`,
  }));

  const handleCreatorChange = (values: string[]) => {
    // Get creator name from the creator address
    const creatorAddress = values[0];
    const creatorName =
      topZoraCreators?.find(({ address }) => address === creatorAddress)?.name ?? '_none_';

    ampli.categoryFilterSelected({
      filter_value: creatorName,
    });

    onFiltersChange({ ...filters, creator: creatorAddress });
  };

  // Used to show/hide the creator filter on temporarily social type selection
  const isZoraPostSelection =
    preset === 'social' &&
    selectedSocialTypes?.length === 1 &&
    selectedSocialTypes[0] === LaunchpadId.ZORA;

  // Used to reset creator filter when social type selection has been confirmed
  const isZoraPostFilter =
    preset === 'social' &&
    filters.launchpads?.length === 1 &&
    filters.launchpads[0] === LaunchpadId.ZORA;

  // Reset creator filter when the filter component is not shown
  useEffect(() => {
    if (!isZoraPostFilter && filters.creator) {
      onFiltersChange({ ...filters, creator: undefined });
    }
  }, [isZoraPostFilter, filters, onFiltersChange]);

  if (!isZoraPostSelection || (!creatorItems.length && !isLoadingCreators)) {
    return null;
  }

  return (
    <Multiselect
      items={creatorItems}
      value={filters.creator && !isLoadingCreators ? [filters.creator] : undefined}
      onChange={handleCreatorChange}
      modal={true}
      triggerVariant="secondary"
      label={{ default: 'Creator', entity: '', Icon: GridIcon }}
      search={{ emptyLabel: 'No creators found' }}
      isLoading={isLoadingCreators}
    />
  );
};
